import React, { useEffect, useId, useRef, useState } from 'react';
import clsx from 'clsx';
import { Calendar } from 'rsuite';
import { FieldShell } from 'components/ui/FieldShell';
import { useFormLocked } from 'components/ui/FormLock';
import { Icon } from 'components/ui/Icon';
import styles from './DateField.module.scss';

function formatDisplay(value: Date | null): string {
  if (!value || Number.isNaN(value.getTime())) return '';
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${month}/${day}/${value.getFullYear()}`;
}

function maskDate(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function parseComplete(text: string): Date | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

export interface IDateFieldProps {
  label: string;
  id: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  onBlur?: () => void;
  onInvalid?: () => void;
  className?: string;
}

export default function DateField({
  label,
  id,
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  placeholder = 'MM/DD/YYYY',
  onBlur,
  onInvalid,
  className,
}: IDateFieldProps) {
  const locked = useFormLocked();
  const isDisabled = disabled || locked;
  const errorId = error ? `${id}-error` : undefined;
  const calendarId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const editing = useRef(false);
  const [text, setText] = useState(() => formatDisplay(value));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (editing.current) return;
    setText(formatDisplay(value));
  }, [value]);

  useEffect(() => {
    if (isDisabled) setOpen(false);
  }, [isDisabled]);

  useEffect(() => {
    if (!open || isDisabled) return undefined;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, isDisabled]);

  const applyText = (raw: string, typing: boolean) => {
    if (isDisabled) return;
    const next = maskDate(raw);
    editing.current = typing;
    setText(next);
    if (!next) {
      onChange(null);
      return;
    }
    const parsed = parseComplete(next);
    if (parsed) {
      onChange(parsed);
      return;
    }
    if (next.length === 10) onInvalid?.();
  };

  return (
    <FieldShell
      label={label}
      id={id}
      error={error}
      disabled={isDisabled}
      required={required}
      className={className}
    >
      <div
        ref={rootRef}
        className={clsx(
          styles.wrap,
          error && styles.wrapError,
          isDisabled && styles.wrapDisabled,
        )}
      >
        <input
          id={id}
          className={styles.input}
          value={text}
          inputMode="numeric"
          autoComplete="bday"
          placeholder={placeholder}
          disabled={isDisabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          onFocus={() => {
            editing.current = true;
          }}
          onChange={event => applyText(event.target.value, true)}
          onBlur={event => {
            applyText(event.target.value, false);
            onBlur?.();
          }}
        />
        {isDisabled ? null : (
          <button
            type="button"
            className={styles.calendarButton}
            aria-label="Open calendar"
            aria-expanded={open}
            aria-controls={calendarId}
            onMouseDown={event => event.preventDefault()}
            onClick={() => setOpen(current => !current)}
          >
            <Icon name="calendar" size={20} />
          </button>
        )}
        {open && !isDisabled ? (
          <div id={calendarId} className={styles.calendar}>
            <Calendar
              compact
              bordered
              value={value || undefined}
              onSelect={date => {
                editing.current = false;
                setText(formatDisplay(date));
                onChange(date);
                setOpen(false);
              }}
            />
          </div>
        ) : null}
      </div>
    </FieldShell>
  );
}
